import { useEffect, useMemo, useState } from 'react';
import type { User } from 'firebase/auth';
import { observeAuth, subscribeCooldownTimers, subscribeDiscardForDay, subscribeDonationRecord, subscribeMember, subscribeSettings, subscribeSosForDay, subscribeWasteForDay } from './data';
import { DEFAULT_DONATION_ITEMS, PRODUCT_TONES } from './defaults';
import { dayKey, previousDayKey, withDerivedProductPricing } from './domain';
import type { AppSettings, CooldownTimer, DiscardEvent, DonationRecord, MemberProfile, SosEntry, WasteEvent } from './types';

export function useAuthUser() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let unsubscribe: () => void = () => undefined;
    try {
      unsubscribe = observeAuth((nextUser) => {
        setUser(nextUser);
        setLoading(false);
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Firebase could not start.');
      setLoading(false);
    }
    return unsubscribe;
  }, []);

  return { user, loading, error };
}

export function useMember(user: User | null, storeId: string | undefined) {
  const [member, setMember] = useState<MemberProfile | null>(null);
  const [loading, setLoading] = useState(Boolean(user && storeId));
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user || !storeId) {
      setMember(null);
      setLoading(false);
      setError('');
      return;
    }

    setMember(null);
    setLoading(true);
    setError('');
    return subscribeMember(storeId, user.uid, (profile) => {
      setMember(profile);
      setLoading(false);
    }, (caught) => {
      setError(caught.message);
      setLoading(false);
    });
  }, [user, storeId]);

  return { member, loading, error };
}

export function useNow() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

export function useOnlineStatus() {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
}

export function useDeviceName() {
  return useMemo(() => {
    const stored = localStorage.getItem('waste-sos-device-name');
    if (stored) return stored;
    const detected = /iPad/.test(navigator.userAgent)
      ? 'iPad'
      : /iPhone/.test(navigator.userAgent)
        ? 'iPhone'
        : 'Web device';
    localStorage.setItem('waste-sos-device-name', detected);
    return detected;
  }, []);
}

export function useStoreData(storeId: string | undefined, now: Date) {
  const today = dayKey(now);
  const previous = previousDayKey(now);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [todayWaste, setTodayWaste] = useState<WasteEvent[]>([]);
  const [previousWaste, setPreviousWaste] = useState<WasteEvent[]>([]);
  const [discardEvents, setDiscardEvents] = useState<DiscardEvent[]>([]);
  const [sosEntries, setSosEntries] = useState<SosEntry[]>([]);
  const [donationRecord, setDonationRecord] = useState<DonationRecord | null>(null);
  const [cooldownTimers, setCooldownTimers] = useState<CooldownTimer[]>([]);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(Boolean(storeId));

  useEffect(() => {
    if (!storeId) return;
    setError('');
    setReady(true);
    const handleError = (caught: { message: string }) => {
      setError(caught.message);
      setReady(true);
    };
    const subscriptions = [
      subscribeSettings(storeId, (value) => {
        setSettings(value ? {
          ...value,
          cooldownTimersEnabled: value.cooldownTimersEnabled ?? false,
          sosEnabled: value.sosEnabled ?? true,
          discardTrackingEnabled: value.discardTrackingEnabled ?? false,
          cardScrubEnabled: value.cardScrubEnabled ?? true,
          products: value.products.map((product) => {
            const normalized = product.id === 'nuggets'
              ? { ...product, menus: ['breakfast', 'lunch'] as typeof product.menus }
              : product;
            return withDerivedProductPricing({
              ...normalized,
              perUnitWeight: normalized.perUnitWeight ?? normalized.averageWeightLb,
              perUnitWeightUnit: normalized.perUnitWeightUnit ?? 'lb',
              tone: PRODUCT_TONES[product.id] ?? product.tone,
            });
          }),
          donationItems: DEFAULT_DONATION_ITEMS.map((defaultItem) => (
            value.donationItems.find((item) => item.id === defaultItem.id) || defaultItem
          )),
        } : value);
      }, handleError),
      subscribeWasteForDay(storeId, today, setTodayWaste, handleError),
      subscribeWasteForDay(storeId, previous, setPreviousWaste, handleError),
      subscribeSosForDay(storeId, today, setSosEntries, handleError),
      subscribeDonationRecord(storeId, today, setDonationRecord, handleError),
      subscribeCooldownTimers(storeId, setCooldownTimers, handleError),
    ];
    return () => subscriptions.forEach((unsubscribe) => unsubscribe());
  }, [storeId, today, previous]);

  useEffect(() => {
    if (!storeId) {
      setDiscardEvents([]);
      return;
    }
    return subscribeDiscardForDay(storeId, today, setDiscardEvents, (caught) => {
      setError(caught.message);
      setReady(true);
    });
  }, [storeId, today]);

  return { settings, todayWaste, previousWaste, discardEvents, sosEntries, donationRecord, cooldownTimers, error, ready, today };
}
